import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useNavigate } from "react-router-dom";
import api, { formatDate } from "../lib/api";
import { toast } from "sonner";
import { 
  Bell, 
  AlertTriangle, 
  Clock, 
  Phone, 
  Mail, 
  Calendar,
  ChevronRight,
  RefreshCw,
  Send,
  User,
  Activity
} from "lucide-react";

const priorityConfig = {
  high: { color: "text-red-400", bg: "bg-red-500/20", border: "border-red-500/30", label: "Urgent" },
  medium: { color: "text-orange-400", bg: "bg-orange-500/20", border: "border-orange-500/30", label: "Medium" },
  low: { color: "text-blue-400", bg: "bg-blue-500/20", border: "border-blue-500/30", label: "Low" }
};

const FollowUpReminders = ({ compact = false }) => {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      const [remindersRes, summaryRes] = await Promise.all([
        api.get("/reminders/stale-leads?days=7"),
        api.get("/reminders/summary")
      ]);
      setReminders(remindersRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.log("Reminders not available");
    } finally {
      setLoading(false);
    }
  };

  const sendDigestEmails = async () => {
    setSendingDigest(true);
    try {
      const response = await api.post("/reminders/send-digest");
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to send reminder digest");
    } finally {
      setSendingDigest(false);
    }
  };

  const displayedReminders = compact ? reminders.slice(0, 5) : reminders;

  if (loading) {
    return (
      <Card className="bg-surface border-white/5">
        <CardContent className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-platinum/30 border-t-platinum rounded-full animate-spin"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-surface border-white/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/20 relative">
              <Bell className="text-orange-400" size={20} />
              {summary?.total_pending > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold">
                  {summary.total_pending > 9 ? "9+" : summary.total_pending}
                </span>
              )}
            </div>
            <div>
              <CardTitle className="text-white font-serif text-lg">Follow-up Reminders</CardTitle>
              <CardDescription>Leads requiring attention</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchReminders}
              className="text-gray-400 hover:text-white"
            >
              <RefreshCw size={14} />
            </Button>
            {!compact && (
              <Button
                onClick={sendDigestEmails}
                disabled={sendingDigest}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
              >
                {sendingDigest ? (
                  <RefreshCw size={14} className="mr-1 animate-spin" />
                ) : (
                  <Send size={14} className="mr-1" />
                )}
                Send Digest
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
              <p className="text-2xl font-bold text-red-400">{summary.high_priority}</p>
              <p className="text-xs text-red-300">Urgent (14+ days)</p>
            </div>
            <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20 text-center">
              <p className="text-2xl font-bold text-orange-400">{summary.medium_priority}</p>
              <p className="text-xs text-orange-300">Medium (7-14 days)</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center">
              <p className="text-2xl font-bold text-blue-400">{summary.low_priority}</p>
              <p className="text-xs text-blue-300">Low (3-7 days)</p>
            </div>
            <div className="p-3 rounded-lg bg-gray-500/10 border border-gray-500/20 text-center">
              <p className="text-2xl font-bold text-gray-400">{summary.no_contact_ever}</p>
              <p className="text-xs text-gray-300">Never Contacted</p>
            </div>
          </div>
        )}

        {/* Reminders List */}
        {reminders.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="text-emerald mx-auto mb-3" size={32} />
            <p className="text-emerald font-medium">All caught up!</p>
            <p className="text-gray-500 text-sm mt-1">No stale leads requiring follow-up</p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayedReminders.map((reminder) => {
              const config = priorityConfig[reminder.priority];
              return (
                <div 
                  key={reminder.id} 
                  className={`p-3 rounded-lg border ${config.border} ${config.bg} hover:bg-white/5 transition-colors cursor-pointer`}
                  onClick={() => navigate("/leads")}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-white font-medium truncate">{reminder.lead_name}</p>
                        <Badge className={`${config.bg} ${config.color} text-xs`}>
                          {config.label}
                        </Badge>
                      </div>
                      <p className="text-gray-400 text-sm truncate">{reminder.lead_email || "No email"}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {reminder.days_since_activity} days since activity
                        </span>
                        {reminder.assigned_owner_name && (
                          <span className="flex items-center gap-1">
                            <User size={12} />
                            {reminder.assigned_owner_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className="bg-white/10 text-gray-300 text-xs">
                        {reminder.lead_status.replace(/_/g, " ")}
                      </Badge>
                      {reminder.last_activity_type && (
                        <span className="text-xs text-gray-500">
                          Last: {reminder.last_activity_type}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {compact && reminders.length > 5 && (
              <Button
                variant="ghost"
                className="w-full text-gray-400 hover:text-white"
                onClick={() => navigate("/leads")}
              >
                View all {reminders.length} reminders
                <ChevronRight size={14} className="ml-1" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FollowUpReminders;
